import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Bars3Icon, XMarkIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  const navigation = [
    { name: 'Home', href: '/' },
    { 
      name: 'Trading', 
      href: '/trading',
      hasDropdown: true,
      dropdownItems: [
        { name: 'Trading Platform', href: '/trading' },
        { name: 'Copy Trading', href: '/copy-trading' },
        { name: 'Bots Trading', href: '/bots-trading' },
        { name: 'Live Trading', href: '/live-trading' },
        { name: 'P2P Trading', href: '/p2p-trading' },
        { name: 'Portfolio', href: '/portfolio' },
      ]
    },
    { 
      name: 'Markets', 
      href: '/markets',
      hasDropdown: true,
      dropdownItems: [
        { name: 'All Markets', href: '/markets' },
        { name: 'Stocks', href: '/markets?type=stocks' },
        { name: 'Cryptocurrency', href: '/markets?type=crypto' },
      ]
    },
    { 
      name: 'Research', 
      href: '/news',
      hasDropdown: true,
      dropdownItems: [
        { name: 'Market Analysis', href: '/news' },
        { name: 'Market News', href: '/news' },
      ]
    },
    { 
      name: 'Company', 
      href: '/about',
      hasDropdown: true,
      dropdownItems: [
        { name: 'About Us', href: '/about' },
        { name: 'Services', href: '/services' },
        { name: 'Contact', href: '/contact' },
      ]
    },
  ];

  const isActiveLink = (href) => {
    return location.pathname === href;
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const shouldBeScrolled = scrollTop > 80;
      if (shouldBeScrolled !== isScrolled) {
        setIsScrolled(shouldBeScrolled);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isScrolled]);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ease-in-out ${
      isScrolled 
        ? 'bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-100' 
        : 'bg-white shadow-sm border-b border-gray-100'
    }`}>
      <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" aria-label="Top">
        <div className={`flex w-full items-center justify-between transition-all duration-300 ease-in-out ${
          isScrolled ? 'py-2 lg:py-3' : 'py-3 lg:py-4'
        }`}>
          {/* Logo */}
          <div className="flex items-center flex-shrink-0">
            <Link to="/" className="flex items-center space-x-2">
              <img 
                src="/intracapital-logos/intracapital-logo.png"
                alt="INTRA CAPITAL LIMITED"
                className={`transition-all duration-300 ease-in-out ${
                  isScrolled ? 'h-10 lg:h-12' : 'h-12 lg:h-14'
                }`}
                style={{ width: 'auto' }}
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex lg:items-center lg:justify-center flex-1 mx-4 xl:mx-8">
            <div className="flex items-center space-x-1 xl:space-x-2">
              {navigation.map((item) => (
                <div key={item.name} className="relative">
                  {item.hasDropdown ? (
                    <div 
                      className="relative"
                      onMouseEnter={() => setOpenDropdown(item.name)}
                      onMouseLeave={() => setOpenDropdown(null)}
                    >
                      <button className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                        openDropdown === item.name 
                          ? 'text-slate-900 bg-slate-50' 
                          : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                      }`}>
                        <span>{item.name}</span>
                        <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform duration-200 ${openDropdown === item.name ? 'rotate-180' : ''}`} />
                      </button>
                      <AnimatePresence>
                        {openDropdown === item.name && (
                          <motion.div
                            initial={{ opacity: 0, y: -8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.15 }}
                            className="absolute left-0 top-full mt-1 w-52 rounded-xl bg-white py-2 shadow-xl ring-1 ring-slate-100 z-50"
                          >
                            {item.dropdownItems?.map((dropdownItem) => (
                              <Link
                                key={dropdownItem.name}
                                to={dropdownItem.href}
                                className="flex items-center px-4 py-2.5 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-50 transition-colors duration-150"
                                onClick={() => setOpenDropdown(null)}
                              >
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mr-3 flex-shrink-0"></span>
                                {dropdownItem.name}
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <Link
                      to={item.href}
                      className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                        isActiveLink(item.href) 
                          ? 'text-slate-900 bg-slate-100' 
                          : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                      }`}
                    >
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Desktop Auth Buttons */}
          <div className="hidden lg:flex lg:items-center lg:space-x-3 flex-shrink-0">
            <a 
              href="http://dashboard.intracapitals.com/" 
              className="text-slate-600 hover:text-slate-900 px-3 py-2 text-sm font-medium transition-colors duration-200 whitespace-nowrap"
            >
              Sign In
            </a>
            <a 
              href="http://dashboard.intracapitals.com/" 
              className="inline-flex items-center px-5 py-2.5 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold rounded-lg transition-all duration-200 whitespace-nowrap shadow-sm hover:shadow-md"
            >
              Open Account
            </a>
          </div>

          {/* Mobile menu button */}
          <div className="flex lg:hidden">
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-md p-2 text-slate-600 hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-slate-500 transition-all duration-200"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              <span className="sr-only">Open main menu</span>
              {isMenuOpen ? (
                <XMarkIcon className="h-6 w-6" />
              ) : (
                <Bars3Icon className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="lg:hidden bg-white border-t border-gray-100 shadow-lg"
            >
              <div className="px-4 py-4 space-y-1 max-h-[calc(100vh-80px)] overflow-y-auto overscroll-contain">
                {navigation.map((item) => (
                  <div key={item.name}>
                    {item.hasDropdown ? (
                      <>
                        <Link
                          to={item.href}
                          className={`block py-3 px-4 text-base font-medium rounded-lg transition-colors duration-200 ${
                            isActiveLink(item.href) 
                              ? 'text-slate-900 bg-slate-100' 
                              : 'text-slate-700 hover:text-slate-900 hover:bg-slate-50'
                          }`}
                          onClick={() => setIsMenuOpen(false)}
                        >
                          {item.name}
                        </Link>
                        <div className="ml-4 mt-1 mb-2 space-y-1 border-l-2 border-amber-200 pl-3">
                          {item.dropdownItems?.map((dropdownItem) => (
                            <Link
                              key={dropdownItem.name}
                              to={dropdownItem.href}
                              className="block py-2 px-3 text-sm text-slate-500 hover:text-slate-700 hover:bg-slate-50 rounded-lg transition-colors duration-200"
                              onClick={() => setIsMenuOpen(false)}
                            >
                              {dropdownItem.name}
                            </Link>
                          ))}
                        </div>
                      </>
                    ) : (
                      <Link
                        to={item.href}
                        className={`block py-3 px-4 text-base font-medium rounded-lg transition-colors duration-200 ${
                          isActiveLink(item.href) 
                            ? 'text-slate-900 bg-slate-100' 
                            : 'text-slate-700 hover:text-slate-900 hover:bg-slate-50'
                        }`}
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {item.name}
                      </Link>
                    )}
                  </div>
                ))}
                <div className="mt-6 pt-4 border-t border-gray-100 space-y-3 pb-4">
                  <a
                    href="http://dashboard.intracapitals.com/"
                    className="block w-full text-center py-3 px-4 text-base font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors duration-200"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Sign In
                  </a>
                  <a
                    href="http://dashboard.intracapitals.com/"
                    className="block w-full text-center py-3 px-4 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-lg transition-colors duration-200"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Open Account
                  </a>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
};

export default Header;
